/**
 * User Settings Service
 * 
 * Handles all user settings-related API calls:
 * - Profile management (email)
 * - Password management
 * - User preferences (default account, pagination)
 * 
 * @validates Requirements - User settings management
 */

import api from './api';
import type {
  ApiResponse,
  User,
  UserPreferences,
  UpdateUserPreferencesRequest,
  UpdateEmailRequest,
  UpdatePasswordRequest,
} from '@budgira/shared';

export const userSettingsService = {
  // ============================================
  // PROFILE
  // ============================================

  /**
   * Get the current user's profile
   */
  async getProfile(): Promise<ApiResponse<User>> {
    const response = await api.get<ApiResponse<User>>('/user/profile');
    return response.data;
  },

  /**
   * Update the user's email address
   * Requires current password verification
   */
  async updateEmail(data: UpdateEmailRequest): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>('/user/email', data);
    return response.data;
  },

  /**
   * Update the user's password
   * Requires current password verification
   */
  async updatePassword(data: UpdatePasswordRequest): Promise<ApiResponse<null>> {
    const response = await api.put<ApiResponse<null>>('/user/password', data);
    return response.data;
  },

  // ============================================
  // PREFERENCES
  // ============================================

  /**
   * Get the user's preferences
   */
  async getPreferences(): Promise<ApiResponse<UserPreferences>> {
    const response = await api.get<ApiResponse<UserPreferences>>('/user/preferences');
    return response.data;
  },

  /**
   * Update the user's preferences
   */
  async updatePreferences(data: UpdateUserPreferencesRequest): Promise<ApiResponse<UserPreferences>> {
    const response = await api.put<ApiResponse<UserPreferences>>('/user/preferences', data);
    return response.data;
  },

  // ============================================
  // ACCOUNT DELETION
  // ============================================

  /**
   * Get the configured deletion delay in days
   */
  async getDeletionDelay(): Promise<ApiResponse<{ delayDays: number }>> {
    const response = await api.get<ApiResponse<{ delayDays: number }>>('/user/deletion-delay');
    return response.data;
  },

  /**
   * Get the current deletion status
   */
  async getDeletionStatus(): Promise<ApiResponse<{ deletionRequestedAt: string; deletionScheduledAt: string } | null>> {
    const response = await api.get<ApiResponse<{ deletionRequestedAt: string; deletionScheduledAt: string } | null>>('/user/deletion-status');
    return response.data;
  },

  /**
   * Request account deletion (30-day GDPR grace period)
   */
  async requestDeletion(): Promise<ApiResponse<{ deletionRequestedAt: string; deletionScheduledAt: string }>> {
    const response = await api.post<ApiResponse<{ deletionRequestedAt: string; deletionScheduledAt: string }>>('/user/request-deletion');
    return response.data;
  },

  /**
   * Cancel a pending deletion request
   */
  async cancelDeletion(): Promise<ApiResponse<null>> {
    const response = await api.post<ApiResponse<null>>('/user/cancel-deletion');
    return response.data;
  },
};

export default userSettingsService;
